/**
 * A trava de rolagem do fundo, quando há diálogo aberto.
 *
 * POR QUE ISTO SAIU DE DENTRO DO COMPONENTE
 *
 * Estava em `gaveta.tsx`, cada diálogo guardando o `overflow` do body na
 * montagem e devolvendo na desmontagem. Com UM diálogo funcionava. Com dois
 * empilhados — e empilhar é rotina aqui: a ficha do cliente abre o modal de
 * "por que perdemos", a lista de números abre o de revogação — o segundo
 * guardava `"hidden"`, porque o primeiro já havia travado.
 *
 * A partir daí:
 *
 *   ficha abre    → guarda ""        → body: hidden
 *   modal abre    → guarda "hidden"  → body: hidden
 *   ficha fecha   → pilha não vazia  → não restaura
 *   modal fecha   → pilha vazia      → restaura "hidden"  ← e trava para sempre
 *
 * O resultado é a tela inteira sem rolagem, sem erro no console e sem nada
 * na interface indicando o que aconteceu. Só um F5 resolve, e quem usa
 * conclui que "o sistema travou".
 *
 * A correção é guardar o valor original UMA vez, na transição de zero para
 * um, e devolvê-lo na volta a zero. Quem entra no meio não guarda nada.
 *
 * Fica em módulo separado, e recebendo o alvo por parâmetro, para poder ser
 * testado sem DOM: o defeito acima é de ORDEM, e ordem se testa com três
 * chamadas de função.
 */

/** O mínimo do `document.body` que esta trava toca. */
export interface AlvoDaTrava {
  readonly style: { overflow: string };
}

/** Pilha de diálogos que pediram a trava. Um símbolo por diálogo aberto. */
const pilha: symbol[] = [];

/** O valor que o body tinha antes do PRIMEIRO diálogo. Só ele é restaurado. */
let overflowOriginal: string | null = null;

function corpo(alvo?: AlvoDaTrava): AlvoDaTrava | null {
  if (alvo) return alvo;
  return typeof document === "undefined" ? null : document.body;
}

/**
 * Trava a rolagem do fundo e devolve a ficha que identifica este diálogo.
 *
 * A ficha é o que permite `destravar` saber se ainda há alguém segurando —
 * e é também o que o Esc usa para responder só no diálogo do topo.
 */
export function travarRolagem(alvo?: AlvoDaTrava): symbol {
  const ficha = Symbol("dialogo");
  const el = corpo(alvo);

  if (pilha.length === 0 && el) {
    // Só o primeiro guarda. O segundo leria "hidden" — que é o defeito.
    overflowOriginal = el.style.overflow;
  }
  pilha.push(ficha);
  if (el) el.style.overflow = "hidden";
  return ficha;
}

/**
 * Solta este diálogo. A rolagem só volta quando o último sai.
 *
 * Restaurar cedo demais destrava o fundo com a gaveta ainda aberta, que é o
 * efeito de "duas rolagens" que a trava existe para evitar.
 */
export function destravarRolagem(ficha: symbol, alvo?: AlvoDaTrava): void {
  const i = pilha.indexOf(ficha);
  if (i >= 0) pilha.splice(i, 1);
  if (pilha.length > 0) return;

  const el = corpo(alvo);
  if (el) el.style.overflow = overflowOriginal ?? "";
  overflowOriginal = null;
}

/** `true` quando esta ficha é a do diálogo do topo — quem responde ao Esc. */
export function ehODoTopo(ficha: symbol): boolean {
  return pilha[pilha.length - 1] === ficha;
}

/** Só para teste: devolve a pilha ao estado inicial entre casos. */
export function zerarTravaParaTeste(): void {
  pilha.length = 0;
  overflowOriginal = null;
}
